import { callFunction } from '@/integrations/supabase/functions';

export interface Verse {
  reference: string;
  text: string;
}

export interface BibleSearchResponse {
  ok?: boolean;
  error?: string;
  // Normalized shape the UI can trust:
  verses?: Verse[];     // for verse/short-passage lookups
  chapter?: string;     // for full-chapter requests
}

// Internal type to hint which shape we expect back
type Mode = 'verses' | 'chapter';

/**
 * Normalize whatever the Edge Function returns into { verses?, chapter? }.
 * Works with either { ok, data } or already-normalized shapes.
 */
function normalizeESV(result: any, mode: Mode): BibleSearchResponse {
  if (!result) return { verses: [], chapter: '' };
  // If already normalized, return as-is
  if ('verses' in result || 'chapter' in result) return result as BibleSearchResponse;

  // If server returned { ok, data }, try to extract
  if (result.ok && result.data) {
    const d = result.data as any;

    // Server may already enrich to { verses }
    if (Array.isArray(d.verses)) return { verses: d.verses };

    // Check for direct chapter text from server
    if (typeof d.chapter === 'string' && mode === 'chapter') {
      return { chapter: d.chapter };
    }

    // Raw ESV payloads often include "passages": string[]
    if (Array.isArray(d.passages)) {
      const joined = d.passages.join('\n\n');

      if (mode === 'chapter') {
        return { chapter: joined };
      }

      // Basic verse splitting fallback if server didn't pre-format verses
      const lines = joined
        .split(/\n+/)
        .map(t => t.trim())
        .filter(Boolean);

      const verses: Verse[] = lines.map((line) => ({
        reference: '', // optional — typically the server should add references
        text: line,
      }));
      return { verses };
    }
  }

  // Unknown shape — return empty but consistent object
  return { verses: [], chapter: '' };
}

/** Fetch individual verses for a given reference (e.g., "John 3:16"). */
export async function fetchPassage(reference: string): Promise<Verse[]> {
  const { data, error, status } = await callFunction<BibleSearchResponse>('bible-search', {
    body: {
      passage: reference,        // <-- send 'passage' (server reads this)
      mode: 'verses' as Mode,    // <-- hint to server how to format back
      source: 'bible-page',
    },
  });

  if (error || status >= 400) {
    throw new Error(`bible-search failed (${status}): ${error?.message ?? ''}`);
  }
  const out = normalizeESV(data, 'verses');
  return out.verses ?? [];
}

/** Fetch the full text of a chapter (e.g., "John 3"). */
export async function fetchChapter(reference: string): Promise<string> {
  const { data, error, status } = await callFunction<BibleSearchResponse>('bible-search', {
    body: {
      passage: reference,        // <-- same field name
      mode: 'chapter' as Mode,   // <-- server formats chapter text
      source: 'bible-page',
    },
  });

  if (error || status >= 400) {
    throw new Error(`bible-search failed (${status}): ${error?.message ?? ''}`);
  }
  const out = normalizeESV(data, 'chapter');
  return out.chapter ?? '';
}

/**
 * (Deprecated) Don't store API keys in the browser. Your Edge Function holds
 * the ESV key securely, so these are no-ops now to avoid leaking secrets.
 */
export function getESVApiKey(): null { return null; }
export function setESVApiKey(_key: string): void { /* no-op */ }

/** Books list retained for UI use. */
export function getBibleBooks(): string[] {
  return [
    'Genesis','Exodus','Leviticus','Numbers','Deuteronomy',
    'Joshua','Judges','Ruth','1 Samuel','2 Samuel','1 Kings','2 Kings',
    '1 Chronicles','2 Chronicles','Ezra','Nehemiah','Esther',
    'Job','Psalms','Proverbs','Ecclesiastes','Song of Solomon',
    'Isaiah','Jeremiah','Lamentations','Ezekiel','Daniel',
    'Hosea','Joel','Amos','Obadiah','Jonah','Micah',
    'Nahum','Habakkuk','Zephaniah','Haggai','Zechariah','Malachi',
    'Matthew','Mark','Luke','John','Acts',
    'Romans','1 Corinthians','2 Corinthians','Galatians','Ephesians',
    'Philippians','Colossians','1 Thessalonians','2 Thessalonians',
    '1 Timothy','2 Timothy','Titus','Philemon','Hebrews',
    'James','1 Peter','2 Peter','1 John','2 John','3 John','Jude','Revelation'
  ];
}